import { useState, useEffect } from "react";
import { supabase } from "../../../lib/supabase";
import { Layers, Check, Plus, X } from "lucide-react";

interface Scope {
  code: string;
  name: string;
}

// Interface for the integration object
interface Integration {
  id: string;
  hotel_id: string;
  scopes: string[];
  status: string;
}

export function FunctionalScope() {
  const [scopes, setScopes] = useState<Scope[]>([]);
  const [selectedScopes, setSelectedScopes] = useState<string[]>([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    loadScopes();
  }, []);

  const loadScopes = async () => {
    try {
      setLoading(true);

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("No authenticated user found");

      // Load the full catalog of scopes (no whitelist) so the page is a faithful
      // mirror of saas_details.scopes.
      const { data: scopesData, error: scopesError } = await supabase
        .from("pms_scopes")
        .select("id, name")
        .order("name");

      if (scopesError) throw scopesError;

      const catalogScopes: Scope[] = (scopesData || []).map((scope) => ({
        code: scope.name.toLowerCase().replace(/\s+/g, "_"),
        name: scope.name,
      }));

      const { data: saasDetails, error: saasError } = await supabase
        .from("saas_details")
        .select("scopes")
        .eq("id", user.id)
        .single();

      if (saasError) throw saasError;

      const savedScopes: string[] = saasDetails?.scopes || [];

      // Any saved code that isn't in the catalog is still surfaced here so the
      // page reflects exactly what's stored in saas_details.scopes (no silent
      // drop, no silent wipe on save).
      const catalogCodes = new Set(catalogScopes.map((s) => s.code));
      const legacyScopes: Scope[] = savedScopes
        .filter((code) => !catalogCodes.has(code))
        .map((code) => ({ code, name: code }));

      setScopes([...catalogScopes, ...legacyScopes]);
      setSelectedScopes(savedScopes);
    } catch (err) {
      console.error("Error loading scopes:", err);
      setError("Failed to load functional scopes");
    } finally {
      setLoading(false);
    }
  };

  const handleScopeToggle = (scopeCode: string) => {
    // If we're deselecting a scope
    if (selectedScopes.includes(scopeCode)) {
      // Remove the scope from selected scopes
      setSelectedScopes((prev) => prev.filter((code) => code !== scopeCode));
    }
    // If we're selecting a scope, just add it
    else {
      setSelectedScopes((prev) => [...prev, scopeCode]);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("No authenticated user found");

      const { error: updateError } = await supabase
        .from("saas_details")
        .update({
          scopes: selectedScopes,
        })
        .eq("id", user.id);

      if (updateError) throw updateError;

      // Now update the integrations table for all connected hotels
      // First, get all integrations for this SaaS, regardless of status
      const { data: integrations, error: integrationsError } = await supabase
        .from("integrations")
        .select("id, hotel_id, scopes, status")
        .eq("saas_id", user.id);

      if (integrationsError) throw integrationsError;
      // For each integration, get the connector scopes and calculate the intersection
      if (integrations && integrations.length > 0) {
        // Get all hotel IDs to fetch their connector scopes
        const hotelIds = integrations.map(
          (integration) => integration.hotel_id
        );

        // Get connector codes for each hotel
        const { data: credentials, error: credentialsError } = await supabase
          .from("credentials")
          .select("hotel_id, connector_code")
          .in("hotel_id", hotelIds)
          .eq("connector_type", "pms")
          .eq("is_primary", true);

        if (credentialsError) throw credentialsError;
        // Create a map of hotel ID to connector code
        const hotelToConnectorMap = new Map();
        if (credentials) {
          credentials.forEach((cred) => {
            hotelToConnectorMap.set(cred.hotel_id, cred.connector_code);
          });
        }

        // Get connector scopes for each connector code
        const connectorCodes = Array.from(
          new Set(
            credentials?.map((cred) => cred.connector_code.toLowerCase()) || []
          )
        );

        const { data: connectors, error: connectorsError } = await supabase
          .from("connectors")
          .select("code, scopes")
          .in("code", connectorCodes);
        if (connectorsError) throw connectorsError;

        // Create map of connector code to scopes
        const connectorToScopesMap = new Map();
        if (connectors) {
          connectors.forEach((connector) => {
            connectorToScopesMap.set(
              connector.code.toLowerCase(),
              connector.scopes || []
            );
          });
        }

        // Update each integration with the new intersection of scopes
        for (const integration of integrations as Integration[]) {
          const connectorCode = hotelToConnectorMap.get(integration.hotel_id);

          if (connectorCode) {
            const connectorScopes =
              connectorToScopesMap.get(connectorCode.toLowerCase()) || [];
            // Calculate intersection of SaaS scopes and connector scopes
            const intersectionScopes = selectedScopes.filter((scope) =>
              connectorScopes.includes(scope)
            );

            // Check if scopes have changed to avoid unnecessary updates
            const scopesChanged =
              JSON.stringify(intersectionScopes.sort()) !==
              JSON.stringify((integration.scopes || []).sort());

            // Only update if scopes have changed
            if (scopesChanged) {
              const { error: updateIntegrationError } = await supabase
                .from("integrations")
                .update({
                  scopes: intersectionScopes,
                })
                .eq("id", integration.id);

            if (updateIntegrationError) {
              console.error(
                `Error updating integration ${integration.id}:`,
                updateIntegrationError
              );
            } else {
              console.log(`Successfully updated integration ${integration.id}`);
            }
            }
          }
        }
      }

      setIsEditing(false);
      setError("Scopes updated successfully");
      setTimeout(() => setError(""), 3000);
    } catch (err: any) {
      console.error("Error saving scopes:", err);
      setError(err.message || "Failed to update scopes");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="extranet-container">
        <div className="extranet-container-loading">
          <div className="px-6 py-4 text-center text-sm text-gray-500">
            Loading functional scopes...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="extranet-container">
      <div className="extranet-container-header">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Layers className="h-6 w-6 text-indigo-600" />
            <h2 className="text-xl font-bold text-gray-600">
              Functional Scope
            </h2>
          </div>
          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="btn-outline-blue inline-flex items-center"
            >
              <Plus className="h-4 w-4 mr-2" />
              Modify Scope
            </button>
          ) : (
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setIsEditing(false);
                  loadScopes();
                }}
                className="btn-secondary inline-flex items-center"
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="btn-primary inline-flex items-center"
              >
                <Check className="h-4 w-4 mr-2" />
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="extranet-container-body">
        {error && (
          <div
            className={`mb-4 p-4 rounded-md ${
              error.includes("successfully") ? "bg-green-50" : "bg-red-50"
            }`}
          >
            <p className="text-sm">{error}</p>
          </div>
        )}

        <div className="grid grid-cols-1 gap-4">
          {scopes.map((scope) => (
            <div
              key={scope.code}
              className={`
                relative flex items-center p-4 rounded-lg border
                ${isEditing ? "cursor-pointer" : ""}
                ${
                  (isEditing && selectedScopes.includes(scope.code)) ||
                  (!isEditing && selectedScopes.includes(scope.code))
                    ? "border-indigo-500 bg-indigo-50"
                    : "border-gray-200"
                }
              `}
              onClick={() => isEditing && handleScopeToggle(scope.code)}
            >
              <div className="flex items-center justify-between w-full">
                <div className="text-sm font-medium text-gray-900">
                  {scope.name}
                </div>
                {((isEditing && selectedScopes.includes(scope.code)) ||
                  (!isEditing && selectedScopes.includes(scope.code))) && (
                  <Check className="h-5 w-5 text-indigo-500" />
                )}
              </div>
            </div>
          ))}
        </div>

        {scopes.length === 0 && (
          <div className="text-center py-8">
            <p className="px-6 py-4 text-center text-sm text-gray-500">
              No functional scopes available.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
